import React, { useState } from "react";
import styled from "styled-components";
import { Table as BsTable } from "react-bootstrap";
import {
  AsistenciaFutura,
  AsistenciaPasada,
  AsistenciaProyectada,
  IAsistenciaProyectada,
  esAsistenciaPasada,
} from "../../../services/asistenciaServicios";
import IconContainer from "../../organisms/IconContainer";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ReagendamientoGuarderiaModal from "../../reagendamiento/ReagendamientoGuarderiaModal";
import ReagendamientoHotelModal from "../../reagendamiento/ReagendamientoHotelModal";
import { stringADate } from "../../../utils/dateUtils";

function Table({
  items,
  fecha,
}: {
  items: AsistenciaProyectada[];
  fecha: string;
}) {
  const [itemAReprogramar, setItemAReprogramar] = useState<
    IAsistenciaProyectada | undefined
  >(undefined);

  const primero = items[0];

  const onCloseReagendamientoModal = () => {
    setItemAReprogramar(undefined);
  };

  const onReagendarClick = (item: IAsistenciaProyectada) => {
    setItemAReprogramar(item);
  };

  return (
    <>
      {!!itemAReprogramar?.idReservaGuarderia && (
        <ReagendamientoGuarderiaModal
          idReserva={itemAReprogramar.idReservaGuarderia!!}
          itemAsistencia={itemAReprogramar}
          fecha={stringADate(fecha)}
          onClose={onCloseReagendamientoModal}
        />
      )}
      {!!itemAReprogramar?.idReservaHotel && (
        <ReagendamientoHotelModal
          idReserva={itemAReprogramar.idReservaHotel!!}
          itemAsistencia={itemAReprogramar}
          fecha={stringADate(fecha)}
          onClose={onCloseReagendamientoModal}
        />
      )}
      {esAsistenciaPasada(primero) ? (
        <TablePasada
          items={items as AsistenciaPasada[]}
          onReagendarClick={onReagendarClick}
        />
      ) : (
        <TableFutura
          items={items as AsistenciaFutura[]}
          onReagendarClick={onReagendarClick}
        />
      )}
    </>
  );
}

function TablePasada({
  items,
  onReagendarClick,
}: {
  items: AsistenciaPasada[];
  onReagendarClick: (item: IAsistenciaProyectada) => void;
}) {
  return (
    <StyledTable responsive hover>
      <thead>
        <tr>
          <StyledColHeader>No.</StyledColHeader>
          <StyledColHeader>Mascota</StyledColHeader>
          <StyledColHeader>Propietario</StyledColHeader>
          <StyledColHeader>Tipo Reserva</StyledColHeader>
          <StyledColHeader>Ingresó</StyledColHeader>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((_, i) => (
          <TableItemPasada
            key={_.idMascota}
            item={_}
            no={i + 1}
            onReagendarClick={onReagendarClick}
          />
        ))}
      </tbody>
    </StyledTable>
  );
}

function TableItemPasada({
  no,
  item,
  onReagendarClick,
}: {
  no: number;
  item: AsistenciaPasada;
  onReagendarClick: (item: IAsistenciaProyectada) => void;
}) {
  return (
    <DynamicRow isSuccess={item.asistio}>
      <StyledCell>{no}</StyledCell>
      <StyledCell>{item.nombreMascota}</StyledCell>
      <StyledCell>{item.nombrePropietario}</StyledCell>
      <StyledCell>{calcularTipoReserva(item)}</StyledCell>
      <StyledCell>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={item.asistio}
            id="flexCheckDefault"
            disabled
          />
        </div>
      </StyledCell>
      <StyledCell>
        {!item.asistio && (
          <IconContainer
            onClick={() => {
              onReagendarClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <EventRepeatIcon />
          </IconContainer>
        )}
      </StyledCell>
    </DynamicRow>
  );
}

function TableFutura({
  items,
  onReagendarClick,
}: {
  items: AsistenciaFutura[];
  onReagendarClick: (item: IAsistenciaProyectada) => void;
}) {
  return (
    <StyledTable responsive hover>
      <thead>
        <tr>
          <StyledColHeader>No.</StyledColHeader>
          <StyledColHeader>Mascota</StyledColHeader>
          <StyledColHeader>Propietario</StyledColHeader>
          <StyledColHeader>Tipo Reserva</StyledColHeader>
        </tr>
      </thead>
      <tbody>
        {items.map((_, i) => (
          <TableItemFutura
            key={_.idMascota}
            item={_}
            no={i + 1}
            onReagendarClick={onReagendarClick}
          />
        ))}
      </tbody>
    </StyledTable>
  );
}

function TableItemFutura({
  no,
  item,
  onReagendarClick,
}: {
  no: number;
  item: AsistenciaFutura;
  onReagendarClick: (item: IAsistenciaProyectada) => void;
}) {
  return (
    <DynamicRow isSuccess={false}>
      <StyledCell>{no}</StyledCell>
      <StyledCell>{item.nombreMascota}</StyledCell>
      <StyledCell>{item.nombrePropietario}</StyledCell>
      <StyledCell>{calcularTipoReserva(item)}</StyledCell>
      <StyledCell>
        <IconContainer
          onClick={() => {
            onReagendarClick(item);
          }}
          style={{ cursor: "pointer" }}
        >
          <EventRepeatIcon />
        </IconContainer>
      </StyledCell>
    </DynamicRow>
  );
}

type TipoReserva = "Guardería" | "Hotel";

function calcularTipoReserva(item: AsistenciaProyectada): TipoReserva {
  if (item.idReservaGuarderia) {
    return "Guardería";
  }
  if (item.idReservaHotel) {
    return "Hotel";
  }

  throw Error(
    "Item de asistencia debe tener idReservaGuarderia o idReservaHotel"
  );
}

function DynamicRow({
  children,
  isSuccess,
}: {
  children: React.ReactNode;
  isSuccess: boolean;
}) {
  if (isSuccess) {
    return <StyledSuccessRow>{children}</StyledSuccessRow>;
  } else {
    return <StyledRow>{children}</StyledRow>;
  }
}

const StyledTable = styled(BsTable)``;

const StyledCell = styled("td")``;

const StyledColHeader = styled("th")`
  color: ${(props) => props.theme.colors.main};
`;

const StyledRow = styled("tr")``;

const StyledSuccessRow = styled(StyledRow)`
  background-color: ${(props) => props.theme.colors.successShadow};
`;

export default Table;
